/**
 * V2 form primitives — light-first inputs used across the auth cluster.
 *
 * Consumed by Login, Signup, ForgotPassword, ResetPassword, AcceptInvite.
 * Keeps a consistent focus ring, error state, and label typography across
 * every form on the V2 surface without pulling in extra libs.
 */
import { forwardRef } from "react";

function joinClasses(...xs) {
  return xs.filter(Boolean).join(" ");
}

export function FieldLabel({ children, htmlFor, className }) {
  return (
    <label
      htmlFor={htmlFor}
      className={joinClasses(
        "block text-v2-caption uppercase tracking-[0.12em] text-v2-textMuted mb-1.5",
        className,
      )}
    >
      {children}
    </label>
  );
}

export const V2Input = forwardRef(function V2Input(
  { label, testId, right, className, tone = "default", ...inputProps },
  ref,
) {
  const toneRing = {
    default: "border-v2-border focus-within:border-v2-accent",
    success: "border-v2-success/60 focus-within:border-v2-success",
    danger: "border-v2-danger/60 focus-within:border-v2-danger",
  }[tone] || "border-v2-border focus-within:border-v2-accent";
  return (
    <div className={joinClasses("min-w-0", className)}>
      {label && <FieldLabel htmlFor={inputProps.id || testId}>{label}</FieldLabel>}
      <div
        className={joinClasses(
          "flex items-center gap-2 px-3.5 py-2.5 rounded-v2-md bg-v2-surface border transition-colors min-w-0",
          toneRing,
        )}
      >
        <input
          ref={ref}
          data-testid={testId}
          id={inputProps.id || testId}
          className="flex-1 min-w-0 w-full bg-transparent outline-none text-v2-text placeholder:text-v2-textSubtle text-v2-body-md"
          {...inputProps}
        />
        {right}
      </div>
    </div>
  );
});

/**
 * V2 primary/secondary/ghost button. Uses accent colour + shadow-lift on hover.
 */
export function V2Button({
  as: Comp = "button",
  variant = "primary",
  className,
  loading = false,
  fullWidth = false,
  children,
  disabled,
  ...rest
}) {
  const styles = {
    primary:
      "bg-v2-accent text-v2-accentInk hover:bg-v2-accentStrong shadow-v2-card hover:shadow-v2-lifted",
    secondary:
      "border border-v2-border text-v2-text hover:bg-v2-surfaceSubtle",
    ghost:
      "text-v2-textMuted hover:text-v2-text",
    danger:
      "bg-v2-danger text-white hover:bg-v2-danger/90 shadow-v2-card",
  }[variant] || "bg-v2-accent text-v2-accentInk";
  return (
    <Comp
      disabled={disabled || loading}
      className={joinClasses(
        "inline-flex items-center justify-center gap-2 rounded-v2-md px-5 py-2.5 text-v2-body-sm font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed",
        fullWidth ? "w-full" : "",
        styles,
        className,
      )}
      {...rest}
    >
      {children}
    </Comp>
  );
}

export function V2Alert({ tone = "info", testId, children }) {
  const styles = {
    info: "bg-v2-infoSoft text-v2-info border-v2-info/30",
    success: "bg-v2-successSoft text-v2-success border-v2-success/30",
    warning: "bg-v2-warningSoft text-v2-warning border-v2-warning/30",
    danger: "bg-v2-dangerSoft text-v2-danger border-v2-danger/30",
  }[tone] || "bg-v2-infoSoft text-v2-info";
  return (
    <div
      data-testid={testId}
      role={tone === "danger" ? "alert" : "status"}
      className={joinClasses("rounded-v2-md border px-3.5 py-2.5 text-v2-body-sm", styles)}
    >
      {children}
    </div>
  );
}

/**
 * Small text link — matches AuthShell secondary CTAs. Not routed here (use
 * <Link>) — this is purely presentational for anchors + button-as-link cases.
 * Consumers are expected to pass children (e.g. link label) via `children` or
 * `{...rest}`; the anchor is intentionally a shell so it can forward all HTML
 * attributes without additional wrapping.
 */
export function V2TextLink({ className, children, ...rest }) {
  return (
    <a
      className={joinClasses(
        "text-v2-accent hover:text-v2-accentStrong transition-colors font-medium",
        className,
      )}
      {...rest}
    >
      {children}
    </a>
  );
}
